import React, { useState, useEffect, useCallback } from 'react';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import api from '../utils/axios';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  Briefcase,
  Search,
  AlertTriangle,
  Clock,
  CheckCircle,
  Archive,
  ShieldAlert,
  Headphones,
  FileText,
  Tag,
  Eye,
  Plus,
  LayoutGrid,
  List,
  RefreshCw,
  User,
  Calendar,
  ArrowRight,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useCustomCaseStatuses } from '../hooks/useCustomCaseStatuses';
import { kanbanClasses } from '../lib/caseStatusMeta';

// Estados del Kanban
const KANBAN_COLUMNS = [
  { id: 'ABIERTO', title: 'Abierto', color: 'border-blue-400', bgColor: 'bg-blue-50', icon: Briefcase },
  { id: 'EN_INVESTIGACION', title: 'En Investigación', color: 'border-yellow-400', bgColor: 'bg-yellow-50', icon: Search },
  { id: 'PENDIENTE_APROBACION', title: 'Pendiente Aprobación', color: 'border-purple-400', bgColor: 'bg-purple-50', icon: Clock },
  { id: 'CERRADO', title: 'Cerrado', color: 'border-green-400', bgColor: 'bg-green-50', icon: CheckCircle },
  { id: 'ARCHIVADO', title: 'Archivado', color: 'border-gray-400', bgColor: 'bg-gray-50', icon: Archive },
];

const PRIORIDADES = {
  CRITICA: { label: 'Crítica', color: 'bg-red-500 text-white' },
  ALTA: { label: 'Alta', color: 'bg-orange-500 text-white' },
  MEDIA: { label: 'Media', color: 'bg-yellow-500 text-white' },
  BAJA: { label: 'Baja', color: 'bg-green-500 text-white' },
};

const TIPOS_CASO = {
  FRAUDE: { label: 'Fraude', color: 'text-red-600', icon: ShieldAlert },
  SOPORTE: { label: 'Soporte', color: 'text-blue-600', icon: Headphones },
  ALERTA: { label: 'Alerta', color: 'text-orange-600', icon: AlertTriangle },
  COMPLIANCE: { label: 'Compliance', color: 'text-purple-600', icon: FileText },
  OTRO: { label: 'Otro', color: 'text-gray-600', icon: Tag },
};

export default function CasesKanban() {
  const navigate = useNavigate();
  const customEstados = useCustomCaseStatuses();
  const ALL_COLUMNS = [
    ...KANBAN_COLUMNS,
    ...customEstados.map(cs => {
      const k = kanbanClasses(cs.colorName);
      return { id: cs.value, title: cs.label, color: k.kborder, bgColor: k.kbg, icon: cs.icon };
    }),
  ];
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterTipo, setFilterTipo] = useState('all');
  const [filterPrioridad, setFilterPrioridad] = useState('all');
  const [confirmDialog, setConfirmDialog] = useState({ open: false, caseItem: null, newStatus: '' });
  const [requiresApproval, setRequiresApproval] = useState(false);
  
  // Para asignación
  const [showAssignDialog, setShowAssignDialog] = useState(false);
  const [pendingMove, setPendingMove] = useState(null);
  const [users, setUsers] = useState([]);
  const [selectedAssignee, setSelectedAssignee] = useState('');
  const [currentUser, setCurrentUser] = useState(null);

  const loadCases = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get('/cases');
      setCases(response.data || []);
    } catch (error) {
      toast.error('Error al cargar casos');
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadUsersAndCurrentUser = useCallback(async () => {
    try {
      const meRes = await api.get('/auth/me');
      setCurrentUser(meRes.data);
      
      // Siempre cargar usuarios para admin
      if (meRes.data.rol === 'ADMINISTRADOR') {
        const usersRes = await api.get('/users');
        // Filtrar usuarios activos (o sin campo activo definido)
        const activeUsers = usersRes.data.filter(u => u.activo === true || u.activo === undefined || u.activo === null);
        setUsers(activeUsers);
      }
    } catch (error) {
    }
  }, []);

  useEffect(() => {
    loadCases();
    loadUsersAndCurrentUser();
  }, [loadCases, loadUsersAndCurrentUser]);

  // Filtrar casos
  const filteredCases = cases.filter(c => {
    const matchSearch = !searchTerm || 
      c.titulo?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      c.descripcion?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      c.id?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchTipo = filterTipo === 'all' || c.tipo === filterTipo;
    const matchPrioridad = filterPrioridad === 'all' || c.prioridad === filterPrioridad;
    return matchSearch && matchTipo && matchPrioridad;
  });

  // Agrupar por estado
  const casesByStatus = ALL_COLUMNS.reduce((acc, col) => {
    acc[col.id] = filteredCases.filter(c => c.estado === col.id);
    return acc;
  }, {});

  // Verificar si requiere aprobación
  const checkApprovalRequired = async (caseId, newStatus) => {
    try {
      const response = await api.get(`/cases/${caseId}/check-transition/${newStatus}`);
      return response.data?.requires_approval || false;
    } catch (error) {
      return false;
    }
  };

  // Manejar drag and drop
  const handleDragEnd = async (result) => {
    if (!result.destination) return;
    
    const { source, destination, draggableId } = result;
    
    if (source.droppableId === destination.droppableId) return;
    
    const caseItem = cases.find(c => c.id === draggableId);
    if (!caseItem) return;

    const newStatus = destination.droppableId;
    
    // Actualizar estado local inmediatamente para feedback visual
    setCases(prev => prev.map(c => 
      c.id === draggableId ? { ...c, estado: newStatus } : c
    ));
    
    // Verificar si el caso NO está asignado
    if (!caseItem.asignado_a) {
      if (currentUser?.rol === 'ADMINISTRADOR') {
        // Revertir el cambio visual mientras se decide
        setCases(prev => prev.map(c => 
          c.id === draggableId ? { ...c, estado: caseItem.estado } : c
        ));
        // Admin: mostrar diálogo para elegir asignación
        setPendingMove({ caseItem, newStatus });
        setSelectedAssignee('');
        setShowAssignDialog(true);
        return;
      } else {
        // Usuario normal: auto-asignar
        try {
          await api.patch(`/cases/${caseItem.id}`, { 
            estado: newStatus,
            asignado_a: currentUser?.id 
          });
          toast.success('Caso movido y asignado a ti');
          loadCases();
          return;
        } catch (error) {
          toast.error('Error al actualizar caso');
          loadCases(); // Recargar para sincronizar
          return;
        }
      }
    }
    
    // Si ya está asignado, verificar aprobación
    const needsApproval = await checkApprovalRequired(caseItem.id, newStatus);
    
    // Revertir cambio visual mientras se confirma
    setCases(prev => prev.map(c => 
      c.id === draggableId ? { ...c, estado: caseItem.estado } : c
    ));
    
    if (needsApproval) {
      setRequiresApproval(true);
      setConfirmDialog({ open: true, caseItem, newStatus });
    } else {
      setRequiresApproval(false);
      setConfirmDialog({ open: true, caseItem, newStatus });
    }
  };

  // Confirmar asignación
  const handleConfirmAssignment = async () => {
    if (!pendingMove) return;
    
    const { caseItem, newStatus } = pendingMove;
    const assigneeId = selectedAssignee || currentUser?.id;
    
    try {
      await api.patch(`/cases/${caseItem.id}`, { 
        estado: newStatus,
        asignado_a: assigneeId 
      });
      
      // Actualizar estado local
      setCases(prev => prev.map(c => 
        c.id === caseItem.id 
          ? { ...c, estado: newStatus, asignado_a: assigneeId }
          : c
      ));
      
      const assigneeName = users.find(u => u.id === assigneeId)?.nombre || currentUser?.nombre;
      toast.success(`Caso movido y asignado a ${assigneeName}`);
      
      // Recargar para sincronizar nombres
      loadCases();
    } catch (error) {
      toast.error('Error al asignar caso');
      loadCases();
    } finally {
      setShowAssignDialog(false);
      setPendingMove(null);
      setSelectedAssignee('');
    }
  };

  // Confirmar cambio de estado
  const confirmStatusChange = async () => {
    const { caseItem, newStatus } = confirmDialog;
    
    try {
      await api.patch(`/cases/${caseItem.id}`, { estado: newStatus });
      
      if (requiresApproval) {
        toast.info('Se ha creado una solicitud de aprobación');
      } else {
        toast.success('Estado actualizado');
      }
      
      loadCases();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al actualizar estado');
    } finally {
      setConfirmDialog({ open: false, caseItem: null, newStatus: '' });
      setRequiresApproval(false);
    }
  };

  const getColumnTitle = (colId) => {
    return ALL_COLUMNS.find(c => c.id === colId)?.title || colId;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('es-AR', { day: '2-digit', month: 'short' });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <RefreshCw className="animate-spin text-slate-400" size={32} />
      </div>
    );
  }

  return (
    <div className="p-6 bg-slate-50 min-h-screen">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
            <LayoutGrid className="text-slate-600" />
            Kanban de Casos
          </h1>
          <p className="text-slate-500 text-sm mt-1">
            Arrastra y suelta para cambiar el estado de los casos
          </p>
        </div>
        
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={() => navigate('/m/gestion-de-casos/casos')}
            className="flex items-center gap-2"
          >
            <List size={16} />
            Vista Lista
          </Button>
          <Button
            onClick={loadCases}
            variant="outline"
            className="flex items-center gap-2"
          >
            <RefreshCw size={16} />
            Actualizar
          </Button>
        </div>
      </div>

      {/* Filtros */}
      <Card className="p-4 mb-6 bg-white">
        <div className="flex flex-wrap gap-4">
          <div className="flex-1 min-w-[200px]">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
              <Input
                placeholder="Buscar casos..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>
          
          <Select value={filterTipo} onValueChange={setFilterTipo}>
            <SelectTrigger className="w-40">
              <SelectValue placeholder="Tipo" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos los tipos</SelectItem>
              {Object.entries(TIPOS_CASO).map(([key, val]) => (
                <SelectItem key={key} value={key}>{val.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          <Select value={filterPrioridad} onValueChange={setFilterPrioridad}>
            <SelectTrigger className="w-40">
              <SelectValue placeholder="Prioridad" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas</SelectItem>
              {Object.entries(PRIORIDADES).map(([key, val]) => (
                <SelectItem key={key} value={key}>{val.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </Card>

      {/* Kanban Board */}
      <DragDropContext onDragEnd={handleDragEnd}>
        <div className="flex gap-4 overflow-x-auto pb-4">
          {ALL_COLUMNS.map((column) => (
            <div key={column.id} className="flex-shrink-0 w-72">
              <div className={`rounded-t-lg p-3 ${column.bgColor} border-t-4 ${column.color}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <column.icon size={18} className="text-slate-600" />
                    <span className="font-semibold text-slate-700">{column.title}</span>
                  </div>
                  <span className="bg-white px-2 py-0.5 rounded-full text-xs font-medium text-slate-600">
                    {casesByStatus[column.id]?.length || 0}
                  </span>
                </div>
              </div>
              
              <Droppable droppableId={column.id} isDropDisabled={false} isCombineEnabled={false} ignoreContainerClipping={false}>
                {(provided, snapshot) => (
                  <div
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                    className={`min-h-[500px] bg-slate-100 rounded-b-lg p-2 transition-colors ${
                      snapshot.isDraggingOver ? 'bg-slate-200' : ''
                    }`}
                  >
                    {casesByStatus[column.id]?.map((caseItem, index) => (
                      <Draggable key={caseItem.id} draggableId={caseItem.id} index={index}>
                        {(provided, snapshot) => (
                          <div
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            {...provided.dragHandleProps}
                            className={`bg-white rounded-lg p-3 mb-2 shadow-sm border border-slate-200 cursor-grab active:cursor-grabbing transition-shadow ${
                              snapshot.isDragging ? 'shadow-lg ring-2 ring-blue-400' : 'hover:shadow-md'
                            }`}
                            data-testid={`kanban-card-${caseItem.id}`}
                          >
                            {/* Card Header */}
                            <div className="flex items-start justify-between gap-2 mb-2">
                              <div className="flex items-center gap-1.5">
                                {TIPOS_CASO[caseItem.tipo] && (
                                  <span className={TIPOS_CASO[caseItem.tipo].color}>
                                    {React.createElement(TIPOS_CASO[caseItem.tipo].icon, { size: 14 })}
                                  </span>
                                )}
                                <span className="text-xs text-slate-400 font-mono">
                                  #{caseItem.id?.slice(0, 6)}
                                </span>
                              </div>
                              {PRIORIDADES[caseItem.prioridad] && (
                                <span className={`text-[10px] px-1.5 py-0.5 rounded ${PRIORIDADES[caseItem.prioridad].color}`}>
                                  {PRIORIDADES[caseItem.prioridad].label}
                                </span>
                              )}
                            </div>
                            
                            {/* Title */}
                            <h4 className="font-medium text-slate-800 text-sm mb-2 line-clamp-2">
                              {caseItem.titulo}
                            </h4>
                            
                            {/* Footer */}
                            <div className="flex items-center justify-between text-xs text-slate-500">
                              <div className="flex items-center gap-1">
                                <User size={12} />
                                <span className="truncate max-w-[80px]">
                                  {caseItem.asignado_a_nombre || 'Sin asignar'}
                                </span>
                              </div>
                              <div className="flex items-center gap-1">
                                <Calendar size={12} />
                                <span>{formatDate(caseItem.created_at)}</span>
                              </div>
                            </div>
                            
                            {/* View Button */}
                            <Button
                              variant="ghost"
                              size="sm"
                              className="w-full mt-2 text-xs h-7"
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/m/gestion-de-casos/caso/${caseItem.id}`);
                              }}
                            >
                              <Eye size={12} className="mr-1" />
                              Ver detalle
                            </Button>
                          </div>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                    
                    {casesByStatus[column.id]?.length === 0 && (
                      <div className="text-center py-8 text-slate-400 text-sm">
                        Sin casos
                      </div>
                    )}
                  </div>
                )}
              </Droppable>
            </div>
          ))}
        </div>
      </DragDropContext>

      {/* Confirm Dialog */}
      <Dialog open={confirmDialog.open} onOpenChange={(open) => !open && setConfirmDialog({ open: false, caseItem: null, newStatus: '' })}>
        <DialogContent className="max-w-md" style={{ backgroundColor: '#ffffff' }}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ArrowRight className="text-blue-600" />
              Cambiar Estado
            </DialogTitle>
            <DialogDescription>
              {requiresApproval 
                ? 'Este cambio de estado requiere aprobación. Se creará una solicitud de aprobación.'
                : '¿Confirmar el cambio de estado?'
              }
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            <div className="bg-slate-50 rounded-lg p-4">
              <p className="text-sm text-slate-600 mb-2">
                <strong>Caso:</strong> {confirmDialog.caseItem?.titulo}
              </p>
              <div className="flex items-center gap-2 text-sm">
                <span className="text-slate-500">
                  {confirmDialog.caseItem?.estado && getColumnTitle(confirmDialog.caseItem.estado)}
                </span>
                <ArrowRight size={16} className="text-slate-400" />
                <span className="font-medium text-slate-700">
                  {getColumnTitle(confirmDialog.newStatus)}
                </span>
              </div>
            </div>
            
            {requiresApproval && (
              <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <p className="text-sm text-amber-800 flex items-center gap-2">
                  <Clock size={16} />
                  Se requiere aprobación para este cambio
                </p>
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setConfirmDialog({ open: false, caseItem: null, newStatus: '' })}
            >
              Cancelar
            </Button>
            <Button onClick={confirmStatusChange}>
              {requiresApproval ? 'Solicitar Aprobación' : 'Confirmar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assign Dialog */}
      <Dialog open={showAssignDialog} onOpenChange={(open) => { if (!open) { setShowAssignDialog(false); setPendingMove(null); }}}>
        <DialogContent className="max-w-md" style={{ backgroundColor: '#ffffff' }}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <User className="text-blue-600" />
              Asignar Caso
            </DialogTitle>
            <DialogDescription>
              El caso no tiene asignado. ¿A quién deseas asignarlo?
            </DialogDescription>
          </DialogHeader>
          
          {pendingMove && (
            <div className="py-4 space-y-4">
              <p className="text-sm text-slate-600">
                Caso: <strong>"{pendingMove.caseItem?.titulo}"</strong>
              </p>
              
              {/* Botón rápido para auto-asignar */}
              <Button 
                className="w-full"
                style={{ backgroundColor: '#0f172a', color: '#ffffff' }}
                onClick={() => {
                  setSelectedAssignee(currentUser?.id);
                  handleConfirmAssignment();
                }}
              >
                <User size={16} className="mr-2" />
                Asignármelo a mí
              </Button>
              
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <span className="w-full border-t border-slate-200" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-white px-2 text-slate-500">O asignar a otro usuario</span>
                </div>
              </div>
              
              <Select value={selectedAssignee} onValueChange={setSelectedAssignee}>
                <SelectTrigger className="w-full" style={{ backgroundColor: '#ffffff' }}>
                  <SelectValue placeholder="Seleccionar usuario..." />
                </SelectTrigger>
                <SelectContent>
                  {users.map(user => (
                    <SelectItem key={user.id} value={user.id}>
                      {user.nombre} {user.id === currentUser?.id ? '(Yo)' : ''} - {user.email}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => { setShowAssignDialog(false); setPendingMove(null); }}
              style={{ backgroundColor: '#ffffff', color: '#475569' }}
            >
              Cancelar
            </Button>
            <Button 
              onClick={handleConfirmAssignment}
              disabled={!selectedAssignee}
              style={{ backgroundColor: '#2563eb', color: '#ffffff' }}
            >
              Asignar y Mover
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
